import { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ReactLenis } from "lenis/react";
import { geoMercator, geoPath } from "d3-geo";
import { feature } from "topojson-client";
import topo from "@/data/india-topo.json";
import {
  ArrowRight,
  ArrowDown,
  Building2,
  Landmark,
  Factory,
  Satellite,
  ShieldCheck,
  Cpu,
  Rocket,
  Wrench,
  Globe2,
  LineChart,
} from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";

// ---- India Map Geometry ----
const MW = 900;
const MH = 900;
const geo = feature(topo, topo.objects.india);
const projection = geoMercator().fitExtent([[40, 40], [MW - 40, MH - 40]], geo);
const pathGen = geoPath(projection);
const STATE_PATHS = geo.features.map((f, i) => ({ d: pathGen(f), key: i }));

const CITIES = [
  { key: "pune", name: "Pune", lon: 73.856, lat: 18.516, tag: "HQ · BUILD", hub: true },
  { key: "mumbai", name: "Mumbai", lon: 72.877, lat: 19.076, tag: "CAPITAL" },
  { key: "ahmedabad", name: "Ahmedabad", lon: 72.571, lat: 23.022, tag: "INDUSTRIAL" },
  { key: "delhi", name: "Delhi", lon: 77.209, lat: 28.613, tag: "POLICY" },
  { key: "bangalore", name: "Bangalore", lon: 77.594, lat: 12.972, tag: "SPACE" },
  { key: "hyderabad", name: "Hyderabad", lon: 78.486, lat: 17.385, tag: "SPACE" },
];
const CITY_PTS = CITIES.map((c) => {
  const [x, y] = projection([c.lon, c.lat]);
  return { ...c, x, y };
});
const PUNE = CITY_PTS.find((c) => c.key === "pune");

const NODES = [
  {
    key: "pune",
    title: "PUNE",
    tag: "BUILD",
    highlight: "Where Arjuna builds.",
    role: "Our centre of gravity",
    icon: Building2,
    bullets: [
      "Headquarters",
      "Engineering",
      "Product development",
      "Deep-tech talent",
      "Aerospace",
      "Manufacturing",
      "Prototyping",
    ],
    accent: "#00E5FF",
    primary: true,
  },
  {
    key: "mumbai",
    title: "MUMBAI",
    tag: "CAPITAL",
    highlight: "Where Arjuna connects with capital.",
    role: "Financial gateway",
    icon: Landmark,
    bullets: [
      "Venture capital",
      "Family offices",
      "Financial institutions",
      "Corporate relationships",
      "Global investor access",
    ],
    accent: "#FF7A1A",
  },
  {
    key: "ahmedabad",
    title: "AHMEDABAD",
    tag: "INDUSTRIAL CONNECTIONS",
    highlight: "Where Arjuna connects with industry.",
    role: "Industrial spine",
    icon: Factory,
    bullets: [
      "Manufacturing",
      "Industrial partnerships",
      "Infrastructure",
      "Enterprise ecosystem",
      "Supply chains",
    ],
    accent: "#22C55E",
  },
  {
    key: "space",
    title: "BANGALORE + HYDERABAD",
    tag: "SPACE ECOSYSTEM",
    highlight: "Where Arjuna accesses the space ecosystem.",
    role: "Space, aerospace & research",
    icon: Satellite,
    bullets: [
      "Space companies",
      "Aerospace talent",
      "Research",
      "ISRO ecosystem",
      "Deep-tech partnerships",
    ],
    accent: "#8B5CF6",
  },
  {
    key: "delhi",
    title: "DELHI",
    tag: "GOVERNMENT + POLICY",
    highlight: "Where Arjuna connects with government and policy.",
    role: "Policy & national programmes",
    icon: ShieldCheck,
    bullets: [
      "Central government",
      "National programmes",
      "Policy",
      "Defence ecosystem",
      "Regulatory relationships",
    ],
    accent: "#F59E0B",
  },
];

const ADVANTAGES = [
  {
    n: "01",
    title: "Capital Efficiency",
    body: "Build the core team in Pune rather than carrying the cost structure of India's most expensive startup ecosystem.",
  },
  {
    n: "02",
    title: "Engineering Depth",
    body: "Access a strong engineering and industrial talent pool aligned with Arjuna's eventual hardware and physical-systems ambitions.",
  },
  {
    n: "03",
    title: "Ecosystem Access",
    body: "Maintain relationships across India's major financial, space, industrial and government hubs without relocating the entire company.",
  },
  {
    n: "04",
    title: "Long-Term Optionality",
    body: "Preserve the ability to expand into hardware, robotics, aerospace, oceans and planetary-scale infrastructure as Arjuna matures.",
  },
];

const EVOLUTION = [
  { label: "AI", icon: Cpu },
  { label: "Earth Observation", icon: Globe2 },
  { label: "Sensors", icon: LineChart },
  { label: "Robotics", icon: Wrench },
  { label: "Aerospace", icon: Rocket },
  { label: "Infrastructure", icon: Building2 },
];

const FOUNDATIONS = [
  "Talent",
  "Engineering",
  "Manufacturing",
  "Research",
  "Customers",
  "Government relationships",
  "Capital",
  "Time",
];

export default function Investors() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <div className="relative min-h-screen bg-arjuna-ink" data-testid="investors-page">
        <Navbar />

        {/* HERO */}
        <section className="relative overflow-hidden pt-28">
          <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_55%_at_15%_15%,rgba(0,229,255,0.14),transparent_60%),radial-gradient(50%_45%_at_85%_25%,rgba(255,122,26,0.08),transparent_60%)]" />
          <div className="container-x relative">
            <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-12">
              <div className="lg:col-span-6">
                <motion.div
                  initial={{ opacity: 0, y: 24 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
                >
                  <span
                    data-testid="investors-eyebrow"
                    className="inline-flex items-center gap-2 rounded-full border border-arjuna-teal/30 bg-arjuna-teal/5 px-3 py-1 font-mono text-[11px] uppercase tracking-[0.28em] text-arjuna-teal"
                  >
                    <LineChart className="h-3.5 w-3.5" /> Investor Thesis · Geography as Operating Model
                  </span>
                  <h1 className="mt-6 font-serif text-4xl leading-[1.05] text-white sm:text-5xl lg:text-[64px] lg:leading-[1.02]">
                    Built in Pune. <br className="hidden sm:block" />
                    <span className="text-arjuna-teal">Connected</span> to India's Deep-Tech Ecosystem.
                  </h1>
                  <p className="mt-6 max-w-2xl text-base text-slate-300 sm:text-lg">
                    Arjuna's headquarters are in Pune. combining engineering
                    talent, industrial capabilities and capital efficiency with
                    direct access to India's financial, space, manufacturing and
                    government ecosystems.
                  </p>
                  <div className="mt-8 flex flex-wrap items-center gap-3">
                    <a
                      href="#invest"
                      data-testid="investors-primary-cta"
                      className="btn-primary group inline-flex items-center gap-2"
                    >
                      Invest in Arjuna
                      <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </a>
                    <a
                      href="#vision"
                      data-testid="investors-secondary-cta"
                      className="btn-ghost inline-flex items-center gap-2"
                    >
                      Explore Our Vision
                    </a>
                  </div>
                </motion.div>
              </div>

              <div className="lg:col-span-6">
                <IndiaNetworkMap />
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 1. Why Pune */}
        <section className="relative border-t border-white/10 py-24" data-testid="section-why-pune">
          <div className="container-x">
            <p className="overline text-arjuna-teal">01 · Why Pune</p>
            <h2 className="mt-4 max-w-4xl font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-[52px] lg:leading-[1.08]">
              Pune gives Arjuna the{" "}
              <span className="text-arjuna-teal">industrial DNA</span> required to
              become a long-term deep-tech company.
            </h2>

            <div className="mt-14 grid grid-cols-1 gap-10 lg:grid-cols-12">
              <div className="space-y-6 text-slate-300 lg:col-span-6">
                <p className="text-base leading-relaxed sm:text-lg">
                  Arjuna's ambition extends beyond software. Over time, the
                  company can evolve across:
                </p>
                <div className="flex flex-wrap gap-2 font-mono text-[11px] uppercase tracking-widest text-slate-200">
                  {["AI", "Earth Observation", "Sensors", "Robotics", "Aerospace", "Physical Systems"].map(
                    (t, i, arr) => (
                      <span key={t} className="inline-flex items-center gap-2">
                        <span className="rounded-md border border-white/10 bg-white/[0.04] px-3 py-1.5">
                          {t}
                        </span>
                        {i < arr.length - 1 && (
                          <ArrowRight className="h-3.5 w-3.5 text-arjuna-teal/60" />
                        )}
                      </span>
                    )
                  )}
                </div>
                <p className="text-base leading-relaxed">
                  That evolution requires more than software engineers. It
                  requires:
                </p>
                <div className="grid grid-cols-2 gap-2 text-sm text-slate-300 sm:grid-cols-3">
                  {[
                    "Aerospace engineering",
                    "Mechanical engineering",
                    "Electronics",
                    "Materials",
                    "Manufacturing",
                    "Prototyping",
                    "Robotics",
                    "Industrial supply chains",
                    "Research talent",
                  ].map((c) => (
                    <div
                      key={c}
                      className="rounded-lg border border-white/10 bg-white/[0.02] px-3 py-2"
                    >
                      {c}
                    </div>
                  ))}
                </div>
                <p className="text-base leading-relaxed">
                  Pune provides an unusually strong concentration of these
                  capabilities while allowing Arjuna to operate with greater
                  capital efficiency than India's most expensive startup hubs.
                </p>
              </div>

              <div className="lg:col-span-6">
                <CapitalEfficiencyCard />
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2. Distributed OS */}
        <section className="relative border-t border-white/10 bg-arjuna-surface/40 py-24" data-testid="section-distributed-os">
          <div className="container-x">
            <p className="overline text-arjuna-teal">02 · Distributed Operating System</p>
            <h2 className="mt-4 max-w-4xl font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-[52px] lg:leading-[1.08]">
              India is our <span className="text-arjuna-teal">distributed operating system.</span>
            </h2>
            <p className="mt-4 max-w-2xl text-base text-slate-400 sm:text-lg">
              Arjuna does not need to put every capability in one city.
            </p>

            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {NODES.map((n, i) => (
                <NodeCard key={n.key} node={n} index={i} />
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 3. Investor Advantage */}
        <section className="relative border-t border-white/10 py-24" data-testid="section-advantage">
          <div className="container-x">
            <p className="overline text-arjuna-teal">03 · The Investor Advantage</p>
            <h2 className="mt-4 max-w-4xl font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-[52px] lg:leading-[1.08]">
              Geography as an{" "}
              <span className="text-arjuna-teal">operating model</span> that reduces
              execution risk.
            </h2>

            <div className="mt-12 grid grid-cols-1 gap-5 md:grid-cols-2">
              {ADVANTAGES.map((a, i) => (
                <motion.div
                  key={a.n}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.2 }}
                  transition={{ duration: 0.6, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                  data-testid={`advantage-${a.n}`}
                  className="group relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.02] p-8 transition-colors hover:border-arjuna-teal/40"
                >
                  <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-arjuna-teal/10 opacity-0 blur-3xl transition-opacity duration-500 group-hover:opacity-100" />
                  <span className="font-mono text-[11px] uppercase tracking-[0.28em] text-arjuna-teal">
                    {a.n}
                  </span>
                  <h3 className="mt-3 font-serif text-2xl text-white sm:text-3xl">
                    {a.title}
                  </h3>
                  <p className="mt-4 max-w-md text-sm leading-relaxed text-slate-400 sm:text-base">
                    {a.body}
                  </p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* SECTION 4. Vision */}
        <section
          id="vision"
          className="relative border-t border-white/10 bg-arjuna-surface/40 py-24"
          data-testid="section-vision"
        >
          <div className="container-x">
            <p className="overline text-arjuna-teal">04 · Optimising For The Company We Want To Become</p>
            <h2 className="mt-4 max-w-4xl font-serif text-3xl leading-tight text-white sm:text-4xl lg:text-[52px] lg:leading-[1.08]">
              We're optimising for the{" "}
              <span className="text-arjuna-teal">company we want to become.</span>
            </h2>

            <div className="mt-12 grid grid-cols-1 items-center gap-8 lg:grid-cols-12">
              <div className="lg:col-span-3">
                <VisionCard
                  eyebrow="TODAY"
                  title="AI + Earth Observation"
                  accent="#00E5FF"
                  align="left"
                />
              </div>

              <div className="lg:col-span-6">
                <div className="rounded-3xl border border-white/10 bg-black/40 p-6">
                  <p className="text-center font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400">
                    Technology Evolution
                  </p>
                  <div className="mt-6 space-y-2">
                    {EVOLUTION.map(({ label, icon: Icon }, i) => (
                      <motion.div
                        key={label}
                        initial={{ opacity: 0, y: 10 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true, amount: 0.5 }}
                        transition={{ duration: 0.45, delay: i * 0.1 }}
                        className="flex flex-col items-center"
                      >
                        <div className="flex w-full max-w-xs items-center gap-3 rounded-xl border border-white/10 bg-white/[0.03] px-4 py-3">
                          <Icon className="h-4 w-4 text-arjuna-teal" />
                          <span className="text-sm font-medium text-white">
                            {label}
                          </span>
                        </div>
                        {i < EVOLUTION.length - 1 && (
                          <ArrowDown className="my-1 h-4 w-4 text-arjuna-teal/50" />
                        )}
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="lg:col-span-3">
                <VisionCard
                  eyebrow="10. 20 YEAR VISION"
                  title="Planetary Intelligence + Physical Systems"
                  accent="#FF7A1A"
                  align="right"
                />
              </div>
            </div>

            <p className="mx-auto mt-14 max-w-3xl text-center text-base leading-relaxed text-slate-300 sm:text-lg">
              Our headquarters strategy is designed around{" "}
              <span className="text-arjuna-teal">the second company</span>. not
              merely the first.
            </p>
          </div>
        </section>

        {/* SECTION 5. Built for 50 years */}
        <section className="relative border-t border-white/10 py-28" data-testid="section-durable">
          <div className="pointer-events-none absolute inset-0 opacity-70 [background:radial-gradient(60%_55%_at_50%_20%,rgba(0,229,255,0.12),transparent_60%)]" />
          <div className="container-x relative">
            <p className="overline text-arjuna-teal">05 · Durability</p>
            <h2 className="mt-4 max-w-5xl font-serif text-4xl leading-[1.02] text-white sm:text-6xl lg:text-[88px]">
              Built for <span className="text-arjuna-teal">50 years</span>, not 5.
            </h2>

            <p className="mt-10 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              A durable deep-tech company needs more than access to capital. It
              needs:
            </p>

            <div className="mt-6 flex flex-wrap gap-2">
              {FOUNDATIONS.map((f) => (
                <span
                  key={f}
                  className="rounded-full border border-arjuna-teal/25 bg-arjuna-teal/5 px-4 py-2 font-mono text-xs uppercase tracking-[0.22em] text-slate-100"
                >
                  {f}
                </span>
              ))}
            </div>

            <div className="mt-16 grid grid-cols-1 gap-6 md:grid-cols-3">
              {[
                { label: "Pune gives us", value: "the foundation." },
                { label: "India gives us", value: "the ecosystem." },
                { label: "The world gives us", value: "the market." },
              ].map((r, i) => (
                <motion.div
                  key={r.label}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, amount: 0.3 }}
                  transition={{ duration: 0.6, delay: i * 0.12 }}
                  className="rounded-3xl border border-white/10 bg-white/[0.02] p-8"
                >
                  <p className="font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400">
                    {r.label}
                  </p>
                  <p className="mt-3 font-serif text-3xl text-white">{r.value}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL STATEMENT */}
        <section id="invest" className="relative border-t border-white/10 py-32" data-testid="section-final">
          <div className="container-x text-center">
            <motion.h2
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.8 }}
              className="mx-auto max-w-5xl font-serif text-4xl leading-[1.02] text-white sm:text-6xl lg:text-[92px]"
            >
              Pune is where engineering meets industry.
              <br />
              <span className="text-arjuna-teal">It is our build advantage.</span>
            </motion.h2>

            <p className="mx-auto mt-8 max-w-2xl text-base leading-relaxed text-slate-300 sm:text-lg">
              Arjuna is deliberately building from an engineering and industrial
              base while maintaining access to India's strongest financial,
              space and government ecosystems.
            </p>

            <div className="mx-auto mt-10 flex max-w-md flex-wrap items-center justify-center gap-3 text-center font-mono text-xs uppercase tracking-[0.28em] text-slate-300">
              <span className="rounded-full border border-arjuna-teal/30 bg-arjuna-teal/5 px-4 py-1.5 text-arjuna-teal">
                One headquarter
              </span>
              <span className="rounded-full border border-white/10 bg-white/[0.03] px-4 py-1.5">
                Multiple ecosystems
              </span>
              <span className="rounded-full border border-[#FF7A1A]/30 bg-[#FF7A1A]/5 px-4 py-1.5 text-[#FF7A1A]">
                Global ambition
              </span>
            </div>

            <div className="mt-12 flex flex-wrap items-center justify-center gap-3">
              <a
                href="mailto:snigdha@arjunaspace.in?subject=Investor%20interest%20-%20Arjuna%20Space%20AI"
                data-testid="investors-final-cta"
                className="btn-primary group inline-flex items-center gap-2"
              >
                Invest in Arjuna
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </a>
              <Link
                to="/"
                data-testid="investors-final-secondary"
                className="btn-ghost inline-flex items-center gap-2"
              >
                Explore Our Vision
              </Link>
            </div>
          </div>
        </section>

        <Footer />
      </div>
    </ReactLenis>
  );
}

// ============ India Network Map ============
function IndiaNetworkMap() {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
      className="relative mx-auto aspect-square w-full max-w-[560px] rounded-3xl border border-white/10 bg-gradient-to-br from-black/50 to-arjuna-surface/40 p-4"
      data-testid="investors-india-map"
    >
      {/* Grid lines */}
      <div className="pointer-events-none absolute inset-0 rounded-3xl grid-lines opacity-30" />

      <svg
        viewBox={`0 0 ${MW} ${MH}`}
        className="relative h-full w-full"
        aria-label="India strategic network map"
      >
        <defs>
          <radialGradient id="pune-glow" cx="50%" cy="50%" r="50%">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.7" />
            <stop offset="70%" stopColor="#00E5FF" stopOpacity="0" />
          </radialGradient>
          <linearGradient id="line-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00E5FF" stopOpacity="0.9" />
            <stop offset="100%" stopColor="#00E5FF" stopOpacity="0.05" />
          </linearGradient>
        </defs>

        {/* India shape */}
        <g>
          {STATE_PATHS.map((s) => (
            <path
              key={s.key}
              d={s.d}
              fill="rgba(0,229,255,0.03)"
              stroke="rgba(0,229,255,0.22)"
              strokeWidth={1.1}
            />
          ))}
        </g>

        {/* Connection lines from Pune */}
        {CITY_PTS.filter((c) => c.key !== "pune").map((c, i) => {
          const len = Math.hypot(c.x - PUNE.x, c.y - PUNE.y);
          return (
            <g key={c.key}>
              <line
                x1={PUNE.x}
                y1={PUNE.y}
                x2={c.x}
                y2={c.y}
                stroke="rgba(0,229,255,0.35)"
                strokeWidth={1.2}
                strokeDasharray="4 6"
              >
                <animate
                  attributeName="stroke-dashoffset"
                  from="0"
                  to="-20"
                  dur="4s"
                  repeatCount="indefinite"
                />
              </line>
              {/* Pulse dot travelling out */}
              <circle r="4" fill="#00E5FF" opacity="0.9">
                <animateMotion
                  dur={`${3.2 + i * 0.4}s`}
                  repeatCount="indefinite"
                  path={`M ${PUNE.x} ${PUNE.y} L ${c.x} ${c.y}`}
                />
                <animate
                  attributeName="opacity"
                  values="0;0.9;0"
                  dur={`${3.2 + i * 0.4}s`}
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          );
        })}

        {/* Pune central glow */}
        <circle cx={PUNE.x} cy={PUNE.y} r="90" fill="url(#pune-glow)" />
        <circle
          cx={PUNE.x}
          cy={PUNE.y}
          r="16"
          fill="none"
          stroke="#00E5FF"
          strokeWidth="1.4"
          opacity="0.6"
        >
          <animate
            attributeName="r"
            values="14;36;14"
            dur="2.6s"
            repeatCount="indefinite"
          />
          <animate
            attributeName="opacity"
            values="0.75;0;0.75"
            dur="2.6s"
            repeatCount="indefinite"
          />
        </circle>

        {/* Other cities */}
        {CITY_PTS.filter((c) => c.key !== "pune").map((c) => (
          <g key={c.key}>
            <circle cx={c.x} cy={c.y} r="7" fill="#0B1220" stroke="#00E5FF" strokeWidth="1.5" />
            <circle cx={c.x} cy={c.y} r="2.4" fill="#00E5FF" />
            <text
              x={c.x + 12}
              y={c.y - 6}
              fill="#E2E8F0"
              fontSize="20"
              fontFamily="ui-monospace, monospace"
              style={{ textTransform: "uppercase", letterSpacing: "0.12em" }}
            >
              {c.name}
            </text>
            <text
              x={c.x + 12}
              y={c.y + 16}
              fill="#00E5FF"
              fontSize="14"
              opacity="0.75"
              fontFamily="ui-monospace, monospace"
              style={{ textTransform: "uppercase", letterSpacing: "0.16em" }}
            >
              {c.tag}
            </text>
          </g>
        ))}

        {/* Pune label */}
        <g>
          <circle cx={PUNE.x} cy={PUNE.y} r="9" fill="#00E5FF" />
          <text
            x={PUNE.x + 16}
            y={PUNE.y - 8}
            fill="#FFFFFF"
            fontSize="28"
            fontWeight="700"
            fontFamily="ui-monospace, monospace"
            style={{ textTransform: "uppercase", letterSpacing: "0.14em" }}
          >
            PUNE
          </text>
          <text
            x={PUNE.x + 16}
            y={PUNE.y + 20}
            fill="#FF7A1A"
            fontSize="15"
            fontFamily="ui-monospace, monospace"
            style={{ textTransform: "uppercase", letterSpacing: "0.18em" }}
          >
            HQ · BUILD
          </text>
        </g>
      </svg>

      <div className="pointer-events-none absolute bottom-4 left-4 flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-400">
        <span className="h-1.5 w-1.5 rounded-full bg-arjuna-teal animate-pulse" />
        Live network · Pune ↔ India
      </div>
    </motion.div>
  );
}

// ============ Capital Efficiency Card ============
function CapitalEfficiencyCard() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
      className="relative overflow-hidden rounded-3xl border border-arjuna-teal/30 bg-gradient-to-br from-arjuna-teal/10 to-black/40 p-8"
      data-testid="capital-efficiency-card"
    >
      <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-arjuna-teal/15 blur-3xl" />
      <p className="overline text-arjuna-teal">Capital Efficiency</p>
      <h3 className="mt-3 font-serif text-3xl text-white sm:text-4xl">
        Build more per <span className="text-arjuna-teal">₹ deployed.</span>
      </h3>
      <p className="mt-4 text-sm text-slate-300 sm:text-base">
        Pune's talent density, industrial base and cost structure mean every
        rupee of capital reaches further. extending runway, unlocking
        experiments and letting Arjuna prioritise deep engineering over burn.
      </p>

      <div className="mt-8 space-y-4">
        <EfficiencyBar city="Pune" pct={92} sub="Arjuna HQ · optimal build cost" accent="#00E5FF" />
        <EfficiencyBar city="Hyderabad" pct={68} sub="Relative cost of talent + ops" accent="#94A3B8" />
        <EfficiencyBar city="Bangalore" pct={48} sub="India's most expensive tech hub" accent="#94A3B8" />
      </div>

      <p className="mt-6 font-mono text-[10px] uppercase tracking-[0.28em] text-slate-500">
        Illustrative · directional efficiency, not a market benchmark
      </p>
    </motion.div>
  );
}

function EfficiencyBar({ city, pct, sub, accent }) {
  return (
    <div>
      <div className="flex items-baseline justify-between">
        <span className="font-mono text-xs uppercase tracking-[0.24em] text-white">{city}</span>
        <span className="font-mono text-xs text-slate-400">{pct}% efficiency</span>
      </div>
      <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-white/5">
        <motion.div
          initial={{ width: 0 }}
          whileInView={{ width: `${pct}%` }}
          viewport={{ once: true, amount: 0.4 }}
          transition={{ duration: 1.1, ease: [0.16, 1, 0.3, 1] }}
          className="h-full rounded-full"
          style={{ background: accent, boxShadow: `0 0 12px ${accent}66` }}
        />
      </div>
      <p className="mt-1 text-[11px] text-slate-500">{sub}</p>
    </div>
  );
}

// ============ Node Card ============
function NodeCard({ node, index }) {
  const Icon = node.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.15 }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.16, 1, 0.3, 1] }}
      data-testid={`node-${node.key}`}
      className={`group relative overflow-hidden rounded-3xl border p-7 transition-colors ${
        node.primary
          ? "border-arjuna-teal/40 bg-gradient-to-br from-arjuna-teal/10 to-black/50 lg:col-span-1"
          : "border-white/10 bg-white/[0.02] hover:border-arjuna-teal/30"
      }`}
    >
      <div
        className="pointer-events-none absolute -right-10 -top-10 h-40 w-40 rounded-full opacity-30 blur-3xl transition-opacity duration-500 group-hover:opacity-60"
        style={{ background: node.accent }}
      />
      <div className="flex items-start justify-between">
        <Icon className="h-6 w-6" style={{ color: node.accent }} />
        <span
          className="rounded-full border px-2.5 py-0.5 font-mono text-[10px] uppercase tracking-widest"
          style={{
            borderColor: `${node.accent}55`,
            color: node.accent,
            background: `${node.accent}0f`,
          }}
        >
          {node.tag}
        </span>
      </div>
      <h3 className="mt-4 font-serif text-2xl text-white sm:text-3xl">{node.title}</h3>
      <p className="mt-1 text-xs uppercase tracking-widest text-slate-400">{node.role}</p>

      <ul className="mt-5 space-y-1.5 text-sm text-slate-300">
        {node.bullets.map((b) => (
          <li key={b} className="flex items-center gap-2">
            <span
              className="h-1 w-1 rounded-full"
              style={{ background: node.accent }}
            />
            {b}
          </li>
        ))}
      </ul>

      <p
        className="mt-6 border-t pt-4 font-mono text-[11px] uppercase tracking-[0.22em]"
        style={{ borderColor: `${node.accent}33`, color: node.accent }}
      >
        {node.highlight}
      </p>
    </motion.div>
  );
}

// ============ Vision Card ============
function VisionCard({ eyebrow, title, accent, align }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration: 0.7 }}
      className={`rounded-3xl border border-white/10 bg-white/[0.02] p-8 ${
        align === "right" ? "text-left lg:text-right" : "text-left"
      }`}
    >
      <p
        className="font-mono text-[10px] uppercase tracking-[0.28em]"
        style={{ color: accent }}
      >
        {eyebrow}
      </p>
      <h3 className="mt-3 font-serif text-2xl leading-tight text-white sm:text-3xl">
        {title}
      </h3>
    </motion.div>
  );
}
